import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { AuthNav, SideNav } from "../../../components/Navbar/Webpages/AuthNav";
import {
  createWithdrawRequest,
  clearCreateWithdrawSuccess,
} from "../../../redux/action";
import { PrimaryButton } from "../../../components/Buttons";
import CurrencyDisplay from "../../../components/Currency/CurrencyDisplay";
import { useCurrency } from "../../../hooks/useCurrency";

// Icons
import {
  FaUniversity,
  FaBitcoin,
  FaWallet,
  FaInfoCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const Withdraw = () => {
  const dispatch = useDispatch();
  const {
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
  } = useCurrency();

  // Local state for UI logic
  const [withdrawalOption, setWithdrawalOption] = useState("bank");
  const [amount, setAmount] = useState(""); // Keep as string for better input handling
  const [error, setError] = useState("");

  const {
    user,
    createWithdrawLoading,
    createWithdrawSuccess,
    createWithdrawError,
  } = useSelector((state) => ({
    user: state.userData.user,
    createWithdrawLoading: state.loadingIndicator.createWithdrawLoading,
    createWithdrawSuccess: state.ajaxStatuses.success.createWithdraw,
    createWithdrawError: state.ajaxStatuses.errors.createWithdraw,
  }));

  // --- Logic Helpers ---

  const validateAmount = (val) => {
    if (!isLoaded) return "Loading exchange rates...";

    const numVal = Number(val);
    if (val === "") return ""; // Allow empty
    if (numVal < 0) return "Enter a positive number";

    // Convert minimum withdrawal to display currency
    const minWithdrawalDisplay = convertUSDToDisplay(10);
    const currencySymbol = getCurrencySymbol();

    if (numVal < minWithdrawalDisplay) {
      return `Minimum withdrawal is ${currencySymbol}${minWithdrawalDisplay.toFixed(
        2
      )}`;
    }

    // Convert user balance to display currency for comparison
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);
    if (numVal > balanceInDisplayCurrency) return "Insufficient balance";

    return "";
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);

    const validationError = validateAmount(val);
    setError(validationError);

    // Convert display currency to USD for API
    if (val && !validationError && isLoaded) {
      const usdValue = convertDisplayToUSD(Number(val));
      formik.setFieldValue("amount", usdValue);
    } else {
      formik.setFieldValue("amount", 0);
    }
  };

  const handleSetPercentage = (percent) => {
    if (!user.balance || !isLoaded) return;

    // Convert user balance to display currency
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);
    const newAmount = (balanceInDisplayCurrency * percent).toFixed(2);

    setAmount(newAmount);

    // Convert back to USD for API
    const usdValue = convertDisplayToUSD(Number(newAmount));
    formik.setFieldValue("amount", usdValue);

    setError(""); // Clear errors on auto-fill
  };

  const formik = useFormik({
    initialValues: {
      type: "Withdrawal",
      amount: "",
      withdrawalOption: "bank",
    },
    onSubmit: (values) => {
      const payload = {
        ...values,
        user: user._id,
        email: user.email,
        userName: user.userName,
        withdrawalOption: withdrawalOption,
      };

      if (!error && amount && !validateAmount(amount) && isLoaded) {
        dispatch(createWithdrawRequest(payload));
      } else if (!amount) {
        setError("Please enter an amount");
      } else if (!isLoaded) {
        setError("Please wait for exchange rates to load");
      }
    },
  });

  // --- Effects ---

  useEffect(() => {
    if (createWithdrawError) toast.error(createWithdrawError);
  }, [createWithdrawError]);

  useEffect(() => {
    if (createWithdrawSuccess) {
      toast.success(createWithdrawSuccess);
      dispatch(clearCreateWithdrawSuccess());
      setAmount(""); // Reset form
    }
  }, [createWithdrawSuccess, dispatch]);

  // --- UI Components ---

  const MethodCard = ({ icon: Icon, label, subLabel, isActive, onClick }) => (
    <div
      onClick={onClick}
      className={`
        cursor-pointer relative flex items-center p-4 rounded-xl border-2 transition-all duration-200
        ${
          isActive
            ? "border-primary bg-primary-50/50 shadow-sm"
            : " hover:border-primary-200 hover:bg-gray-50"
        }
      `}
    >
      <div
        className={`
        w-10 h-10 rounded-full flex items-center justify-center mr-4 text-lg
        ${isActive ? "bg-primary text-white" : "bg-gray-100 text-gray-500"}
      `}
      >
        <Icon />
      </div>
      <div className="flex-1">
        <h3
          className={`font-bold text-sm ${
            isActive ? "text-primary-900" : "text-gray-700"
          }`}
        >
          {label}
        </h3>
        <p className="text-xs text-gray-500">{subLabel}</p>
      </div>
      {isActive && (
        <div className="absolute top-3 right-3 w-3 h-3 bg-primary-500 rounded-full animate-pulse" />
      )}
    </div>
  );

  return (
    <div className="bg-gray-50/50 min-h-screen">
      <AuthNav showLogo={false} title="Withdraw Funds" />
      <SideNav />

      <div className="container mx-auto max-w-xl px-4 py-8 md:py-12 pt-20">
        {/* Balance Header */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-500 font-medium mb-1">
            Available Balance
          </p>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 tracking-tight">
            <CurrencyDisplay usdAmount={user.balance} className="inline" />
          </h1>
        </div>

        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden border ">
          <form onSubmit={formik.handleSubmit} className="p-6 md:p-8">
            {/* Loading indicator for rate fetching */}
            {!isLoaded && (
              <div className="mb-4 p-3 bg-blue-50 text-blue-600 text-sm rounded-lg text-center">
                Loading exchange rates...
              </div>
            )}

            {/* 1. Method Selection */}
            <div className="mb-8">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Select Destination
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <MethodCard
                  icon={FaUniversity}
                  label="Direct Deposit"
                  subLabel="1-3 Business Days"
                  isActive={withdrawalOption === "bank"}
                  onClick={() => setWithdrawalOption("bank")}
                />
                <MethodCard
                  icon={FaBitcoin}
                  label="Crypto Wallet"
                  subLabel="Instant Transfer"
                  isActive={withdrawalOption === "crypto"}
                  onClick={() => setWithdrawalOption("crypto")}
                />
              </div>
            </div>

            {/* 2. Amount Input */}
            <div className="mb-8">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                Withdrawal Amount
              </label>

              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-2xl font-light">
                  {getCurrencySymbol()}
                </span>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full bg-gray-50 border-none rounded-2xl py-4 pl-10 pr-4 text-3xl font-bold text-gray-900 placeholder-gray-300 focus:ring-2 focus:ring-primary-500/20 focus:bg-white transition-all outline-none"
                />
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-3">
                {[0.25, 0.5, 1].map((percent) => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => handleSetPercentage(percent)}
                    className="flex-1 py-2 text-xs font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    {percent === 1 ? "Max" : `${percent * 100}%`}
                  </button>
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 mt-3 text-red-500 text-sm animate-pulse">
                  <FaInfoCircle /> {error}
                </div>
              )}
            </div>

            {/* 3. Destination Details Check */}
            <div className="mb-8 p-4 bg-gray-50 rounded-xl border ">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Destination</span>
                <span className="text-sm font-bold text-gray-700 capitalize">
                  {withdrawalOption === "bank"
                    ? "Bank Account"
                    : "Crypto Wallet"}
                </span>
              </div>

              {withdrawalOption === "bank" ? (
                user.bankName && user.accountNumber && user.accountName ? (
                  <div className="space-y-2">
                    {user.bankName && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Bank Name</span>
                        <span className="text-sm font-mono text-gray-900">
                          {user.bankName}
                        </span>
                      </div>
                    )}
                    {user.accountName && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          Account Name
                        </span>
                        <span className="text-sm font-mono text-gray-900">
                          {user.accountName}
                        </span>
                      </div>
                    )}
                    {user.accountNumber && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          Account Number
                        </span>
                        <span className="text-sm font-mono text-gray-900">
                          ****{user.accountNumber.slice(-4)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Account</span>
                    <Link
                      to="/dashboard/account"
                      className="flex items-center gap-1 text-sm text-red-500 font-semibold hover:underline"
                    >
                      <FaExclamationTriangle /> Add Bank Info
                    </Link>
                  </div>
                )
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Wallet Address</span>
                  <div className="text-right">
                    {user.walletAddress ? (
                      <span className="text-sm font-mono text-gray-900">
                        {user.walletAddress.slice(0, 6)}...
                        {user.walletAddress.slice(-4)}
                      </span>
                    ) : (
                      <Link
                        to="/dashboard/account"
                        className="flex items-center gap-1 text-sm text-red-500 font-semibold hover:underline"
                      >
                        <FaExclamationTriangle /> Add Wallet
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 4. Action */}
            <PrimaryButton
              action={formik.handleSubmit}
              loading={createWithdrawLoading}
              text={
                createWithdrawLoading
                  ? "Processing..."
                  : `Withdraw ${amount ? amount : ""}`
              }
              disabled={
                !amount ||
                !!error ||
                Number(amount) <= 0 ||
                !isLoaded ||
                (withdrawalOption === "crypto" && !user.walletAddress) ||
                (withdrawalOption === "bank" &&
                  (!user.bankName || !user.accountNumber || !user.accountName))
              }
            />

            {/* Footer Info */}
            <div className="mt-6 flex items-center justify-center gap-6 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <FaWallet /> Min{" "}
                {isLoaded
                  ? `${getCurrencySymbol()}${convertUSDToDisplay(10).toFixed(
                      2
                    )}`
                  : "Loading..."}
              </span>
              <span className="flex items-center gap-1">
                <FaInfoCircle /> No Fee
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Withdraw;
